import { useCallback, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Loader2, LogIn, Search, UserCheck } from 'lucide-react';
import type { Participant, Presence, PresenceEquipment } from 'shared';
import { useDigitalCenters } from '@/hooks/useDigitalCenters';
import { useParticipants } from '@/hooks/useParticipants';
import {
  useCheckInParticipant,
  useCheckOutParticipant,
  usePresenceEquipment,
  usePresences,
} from '@/hooks/usePresences';
import EquipmentPickerModal from '@/components/presences/EquipmentPickerModal';
import PresenceCard from '@/components/presences/PresenceCard';
import { ConfirmDialog, ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';
import { activeVisitCentre, presenceErrorMessage } from '@/lib/presenceErrors';

const SEARCH_RESULTS = 8;
const CENTRE_KEY = 'presencas.centro';

/** The centre last chosen on this browser, so the desk opens on its own. */
function rememberedCentre(): number | null {
  try {
    const id = Number(window.localStorage.getItem(CENTRE_KEY));
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function rememberCentre(id: number) {
  try {
    window.localStorage.setItem(CENTRE_KEY, String(id));
  } catch {
    // Private browsing: the choice lasts for this visit only.
  }
}

/**
 * The front desk. For a participant who has no phone with them, a member of
 * staff does here what the participant would do in the app: check in, take
 * equipment and give it back, and leave. The rules are the app's own, and
 * each action is recorded against whoever took it.
 */
export default function PresencasPage() {
  const { toasts, toast, dismiss } = useToast();

  const centersQuery = useDigitalCenters({ limit: 100 });
  const centers = centersQuery.data?.digitalCenters ?? [];
  const [chosenCentre, setChosenCentre] = useState<number | null>(rememberedCentre);
  // The remembered centre may be gone; with one centre there is no choice.
  const centreId =
    centers.find((c) => c.id === chosenCentre)?.id ?? (centers.length > 0 ? centers[0].id : null);

  const presencesQuery = usePresences(centreId);
  const present = presencesQuery.data?.presences ?? [];

  const checkIn = useCheckInParticipant();
  const checkOut = useCheckOutParticipant();
  const equipment = usePresenceEquipment();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value.trim()), 350);
  }, []);
  const searching = debouncedSearch.length >= 2;
  const resultsQuery = useParticipants({
    limit: SEARCH_RESULTS,
    search: searching ? debouncedSearch : '',
  });
  const presentIds = new Set(present.map((p) => p.participantId));
  const results = searching
    ? (resultsQuery.data?.participants ?? []).filter((p) => !presentIds.has(p.id))
    : [];

  const [picking, setPicking] = useState<Presence | null>(null);
  const [leaving, setLeaving] = useState<Presence | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const handleCheckIn = async (participant: Participant) => {
    if (centreId === null) return;
    const name = `${participant.nome} ${participant.apelido}`;
    setBusyId(participant.id);
    try {
      await checkIn.mutateAsync({ participantId: participant.id, digitalCenterId: centreId });
      toast.success(`Entrada de ${name} registada.`);
      setSearch('');
      setDebouncedSearch('');
    } catch (err: unknown) {
      const where = activeVisitCentre(err);
      toast.error(
        where
          ? `${name} já está em ${where}. Termina essa visita primeiro.`
          : presenceErrorMessage(err, 'Não foi possível registar a entrada.'),
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleHandOver = async (presence: Presence, equipmentId: number, name: string) => {
    setBusyId(presence.participantId);
    try {
      await equipment.mutateAsync({
        participantId: presence.participantId,
        equipmentId,
        action: 'start',
      });
      toast.success(`${name} entregue a ${presence.participantName}.`);
      setPicking(null);
    } catch (err: unknown) {
      toast.error(presenceErrorMessage(err, 'Não foi possível entregar o equipamento.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleTakeBack = async (presence: Presence, held: PresenceEquipment) => {
    setBusyId(presence.participantId);
    try {
      await equipment.mutateAsync({
        participantId: presence.participantId,
        equipmentId: held.equipmentId,
        action: 'end',
      });
      toast.success(`Devolução de ${held.equipmentName} registada.`);
    } catch (err: unknown) {
      toast.error(presenceErrorMessage(err, 'Não foi possível registar a devolução.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleCheckOut = async (presence: Presence) => {
    setBusyId(presence.participantId);
    try {
      await checkOut.mutateAsync(presence.participantId);
      toast.success(`Visita de ${presence.participantName} terminada.`);
    } catch (err: unknown) {
      toast.error(presenceErrorMessage(err, 'Não foi possível terminar a visita.'));
    } finally {
      setBusyId(null);
      setLeaving(null);
    }
  };

  // The picker shows the participant as the list has them now, so that what
  // they already hold is up to date after each hand-over.
  const pickingNow = picking
    ? (present.find((p) => p.participantId === picking.participantId) ?? picking)
    : null;

  return (
    <>
      <Helmet>
        <title>Presenças | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-primary" />
              Presenças
            </h1>
            <p className="text-muted-foreground mt-1 max-w-2xl">
              Para quem não trouxe telemóvel: regista aqui a entrada, o equipamento e a saída. As
              regras são as da app, e cada registo fica com o teu nome.
            </p>
          </div>

          {centers.length > 1 && (
            <div className="flex flex-col gap-1">
              <label htmlFor="presences-centre" className="text-xs font-medium text-muted-foreground">
                Centro Digital
              </label>
              <select
                id="presences-centre"
                className="input"
                value={centreId ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setChosenCentre(id);
                  rememberCentre(id);
                }}
              >
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.centerName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {centersQuery.isLoading && <LoadingBlock />}
        {centersQuery.isError && <ErrorBlock message="Não foi possível carregar os centros." />}
        {!centersQuery.isLoading && !centersQuery.isError && centers.length === 0 && (
          <ErrorBlock message="Ainda não há nenhum Centro Digital registado." />
        )}

        {centreId !== null && (
          <>
            <section className="card p-6 space-y-3" aria-labelledby="check-in-heading">
              <h2 id="check-in-heading" className="text-lg font-semibold text-foreground">
                Registar entrada
              </h2>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="search"
                  className="input pl-9 w-full"
                  placeholder="Nome, apelido ou email…"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-label="Procurar participante para registar a entrada"
                />
              </div>

              {searching && resultsQuery.isLoading && (
                <p className="text-sm text-muted-foreground flex items-center gap-2" role="status">
                  <Loader2 className="w-4 h-4 animate-spin" />A procurar…
                </p>
              )}
              {searching && !resultsQuery.isLoading && results.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nenhum participante encontrado fora do centro.
                </p>
              )}
              {results.length > 0 && (
                <ul className="divide-y divide-border rounded-lg border border-border max-w-2xl">
                  {results.map((p) => {
                    const name = `${p.nome} ${p.apelido}`;
                    const active = p.estadoRegisto === 'Ativo';
                    return (
                      <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{name}</p>
                          <p className="text-xs text-muted-foreground">
                            ID {p.id}
                            {!active && ` · registo ${p.estadoRegisto.toLowerCase()}`}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCheckIn(p)}
                          disabled={!active || busyId !== null}
                          title={
                            active ? undefined : 'Só quem tem o registo ativo pode entrar no centro'
                          }
                          aria-label={`Registar entrada de ${name}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                        >
                          {busyId === p.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <LogIn className="w-4 h-4" />
                          )}
                          Registar entrada
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="space-y-3" aria-labelledby="present-heading">
              <h2 id="present-heading" className="text-lg font-semibold text-foreground">
                No centro agora
                <span className="ml-2 text-sm font-normal text-muted-foreground tabular-nums">
                  {present.length}
                </span>
              </h2>

              {presencesQuery.isLoading && <LoadingBlock />}
              {presencesQuery.isError && (
                <ErrorBlock message="Não foi possível carregar quem está no centro." />
              )}
              {!presencesQuery.isLoading && !presencesQuery.isError && present.length === 0 && (
                <p className="card p-6 text-sm text-muted-foreground text-center">
                  Ninguém no centro. A lista actualiza-se sozinha.
                </p>
              )}

              {present.length > 0 && (
                <ul className="space-y-3">
                  {present.map((p) => (
                    <PresenceCard
                      key={p.visitId}
                      presence={p}
                      busy={busyId === p.participantId}
                      onHandOver={() => setPicking(p)}
                      onTakeBack={(held) => handleTakeBack(p, held)}
                      onCheckOut={() => setLeaving(p)}
                    />
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      {pickingNow && (
        <EquipmentPickerModal
          presence={pickingNow}
          pendingId={equipment.isPending ? (equipment.variables?.equipmentId ?? null) : null}
          onPick={(equipmentId, name) => handleHandOver(pickingNow, equipmentId, name)}
          onClose={() => setPicking(null)}
        />
      )}

      {leaving && (
        <ConfirmDialog
          title="Terminar visita"
          confirmLabel="Terminar visita"
          tone="primary"
          isPending={checkOut.isPending}
          onConfirm={() => handleCheckOut(leaving)}
          onClose={() => setLeaving(null)}
        >
          Terminar a visita de{' '}
          <span className="font-semibold text-foreground">{leaving.participantName}</span> a{' '}
          {leaving.digitalCenterName}?
        </ConfirmDialog>
      )}

      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
